import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Typography, Button, Select, Tag } from 'antd';
import Tick from '../../../../../assets/customSVG/Tick';
import { useAspSelector } from '../../../../../test/jest-redux-hooks';
import { inboxTeamMembers } from '../../../../inbox/api/Api';

const { Text } = Typography;
const { Option } = Select;

const AssigneePreview = ({
  node,
  onUpdateAssignees,
  isUpdating = false,
  handleOk,
  allowToEditFlow,
}) => {
  const { currentBrand } = useAspSelector((state) => state.app);

  const [error, setError] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState([]); // can be [{id, user_name, email}, ...] OR [id, ...]
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Initialize selected assignee from node data (back-compat aware)
  useEffect(() => {
    if (node?.data?.operations?.assignee?.length) {
      setSelectedAssignees(node.data.operations.assignee);
    } else if (node?.data?.operations?.assignee?.length) {
      setSelectedAssignees(node.data.operations.assignee);
    } else if (node?.data?.operations?.assignee) {
      setSelectedAssignees([{ id: node.data.operations.assignee }]);
    } else {
      setSelectedAssignees([]);
    }
  }, [node?.data?.operations]);

  // Fetch team members for current brand
  useEffect(() => {
    if (!currentBrand?.brand_id) return;

    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const response = await inboxTeamMembers(currentBrand.brand_id, null, true);
        const list = Array.isArray(response?.data?.users) ? response.data.users : [];
        // dedupe by id
        const unique = Array.from(new Map(list.map((u) => [u.id, u])).values());
        if (alive) setTeamMembers(unique);
      } catch (err) {
        console.error('Error fetching team members:', err);
        if (alive) setError('Failed to load team members. Please try again.');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [currentBrand?.brand_id]);

  // Build a readable name for a given id (fallback to id if not found)
  const getAssigneeName = (assigneeIdOrObj) => {
    if (assigneeIdOrObj && typeof assigneeIdOrObj === 'object') {
      const nm = assigneeIdOrObj.user_name || assigneeIdOrObj.id;
      return assigneeIdOrObj.email ? `${nm} (${assigneeIdOrObj.email})` : String(nm);
    }
    const id = assigneeIdOrObj;
    const member = teamMembers.find((m) => String(m.id) === String(id));
    return member ? `${member.user_name} (${member.email})` : String(id);
  };

  // Convert current selection (selectedAssignees) into AntD Select labelInValue items
  const currentSelectValues = React.useMemo(() => {
    return (selectedAssignees || []).map((a) => {
      if (a && typeof a === 'object') {
        const id = a.id ?? a.value ?? a.key;
        return {
          key: String(id),
          value: String(id),
          label: getAssigneeName(a),
          ...a,
        };
      }
      // primitive id
      return {
        key: String(a),
        value: String(a),
        label: getAssigneeName(a),
      };
    });
  }, [selectedAssignees, teamMembers]);

  // Custom tag renderer with modern design
  const tagRender = ({ label, value, closable, onClose, isMaxTag }) => {
    // Handle the overflow tag ("+X ..." tag)
    if (isMaxTag) {
      return (
        <span className="inline-flex items-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 text-xs font-[500] px-3 py-2 rounded-full ml-1 border border-blue-200">
          {label}
        </span>
      );
    }
    
    // Don't render anything if the value is empty or undefined
    if (!value || !label) {
      return null;
    }
    
    const assigneeName = String(label || '');
    const nameInitial = assigneeName ? assigneeName.charAt(0).toUpperCase() : 'U';
    
    // Extract email from the label (format: "Name (email@example.com)")
    const emailMatch = assigneeName.match(/\(([^)]+)\)/);
    const displayText = emailMatch ? emailMatch[1] : assigneeName;
    
    return (
      <span className="inline-flex my-[4px] items-center text-[12px] bg-gradient-to-r from-white to-gray-50 border border-gray-200 text-gray-800 text-sm font-[500] px-3 py-1.5 rounded-full hover:from-gray-50 hover:to-gray-100 hover:border-gray-300 transition-all duration-200 mr-2 shadow-sm hover:shadow-md">
        <span className="h-6 w-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white flex items-center justify-center text-xs font-[600] mr-2 shadow-sm">
          {nameInitial}
        </span>
        <span className="max-w-[140px] truncate text-xs">{displayText}</span>
        {closable && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className="ml-2 text-gray-400 hover:text-red-500 focus:outline-none transition-colors duration-200"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </span>
    );
  };

  // This mirrors the "Assignee" component's onChange shape and then reuses the existing preview handler
  const emitUpdateNodePayload = (selectedMembers) => {
    const typeKey = node?.type?.toLowerCase();

    const payload = {
      key: 'data',
      data: {
        type: typeKey,
        [typeKey]: selectedMembers,
        operations: {
          assignee: selectedMembers,
        },
      },
    };

    // This matches how Assignee used to call back into the preview
    handleAssigneeChange({
      type: 'UPDATE_NODE',
      id: node?.uniq_id,
      ...payload,
    });
  };

  // Equivalent to the old `handleAssigneeChange` that consumed the child payload
  const handleAssigneeChange = useCallback(
    ({ type, id, ...payload }) => {
      if (type === 'UPDATE_NODE') {
        const assignee =
          payload?.data?.operations?.assignee ??
          [];

        setSelectedAssignees(assignee);

        if (onUpdateAssignees) {
          onUpdateAssignees({
            operations: {
              assignee: assignee,
            },
          });
        }
      }
    },
    [onUpdateAssignees]
  );

  // When user changes selection in the Select
  const handleSelectChange = (values /* array of {value,label,key} */) => {
    // Normalize to full objects if possible (enrich from teamMembers)
    const selectedMembers = values.map((v) => {
      const id = v?.value ?? v?.key;
      const found = teamMembers.find((m) => String(m.id) === String(id));
      if (found) return found;
      // fallback minimal object
      return { id, user_name: v?.label, email: undefined };
    });

    emitUpdateNodePayload(selectedMembers);
  };

  const handleSubmit = useCallback(() => {
    const selectedMembers = currentSelectValues.map(value => {
      if (typeof value === 'object') {
        // Handle the case where we have key/value objects
        if (value.key && value.value) {
          return {
            id: value.value,
            key: value.key,
            label: value.label
          };
        }
        return value; // Already have full object
      }
      return teamMembers.find(member => member.id.toString() === value.toString()) || { id: value };
    });

    const data = {
      ...node?.data,
      type: 'assignee',
      assignee: selectedMembers,
      operations: {
        assignee: selectedMembers,
      },
    };

    if (handleOk) {
      handleOk(data
        // `Assigned to ${selectedAssignees.length} team member${
        //   selectedAssignees.length !== 1 ? 's' : ''
        // }`
      );
    }
  }, [handleOk, selectedAssignees]);

  return (
    <div className="p-4" style={{ width: '370px' }}>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          className="mb-4"
          closable
          onClose={() => setError('')}
        />
      )}

      {/* Inlined Assignee UI (copied functionality) */}
      <div className=" rounded-lg">
        <Text type="" className="block font-normal text-[14px] mb-4">
          Assign this conversation to team members who will be responsible for following up.
        </Text>

        <div className="">
          <Text className="block text-sm font-medium text-gray-700 mb-2">
            Select Team Members
          </Text>
          <div className="relative">
            <Select
              mode="multiple"
              className="w-full !ring-0 hover:!ring-0 focus:!ring-0 focus:!ring-offset-0 focus:!border-transparent hover:!border-gray-300"
              style={{
                minHeight: '48px'
              }}
              dropdownMatchWidth={false}
              suffixIcon={null}
              placeholder={
                <div className="flex items-center text-gray-500 py-1">
                  <svg className="h-5 w-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="font-[500]">Search and select team members...</span>
                </div>
              }
              value={currentSelectValues}
              onChange={handleSelectChange}
              loading={loading}
              showSearch
              optionFilterProp="searchText"
              filterOption={(input, option) => {
                const searchText = option?.searchText?.toLowerCase() || '';
                const inputLower = input.toLowerCase();
                return searchText.includes(inputLower);
              }}
              tagRender={tagRender}
              optionLabelProp="label"
              labelInValue
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
              dropdownClassName="rounded-xl shadow-xl border-0 [&_.ant-select-item-option-selected]:!bg-transparent [&_.ant-select-item-option-active]:!bg-transparent [&_.ant-select-item-option-selected_.ant-select-item-option-state]:hidden"
              dropdownStyle={{
                borderRadius: '12px',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                // border: 'none',
                overflow: 'hidden'
              }}
              listHeight={300}
              size="large"
              disabled={!allowToEditFlow}
              notFoundContent={
                <div className="py-8 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  <p className="text-gray-500 font-[500]">No team members found</p>
                  <p className="text-gray-400 text-sm mt-1">Try adjusting your search</p>
                </div>
              }
            >
              {teamMembers.map((member) => {
                const displayName = `${member.user_name} (${member.email})`;
                const searchText = `${member.user_name} ${member.email}`.toLowerCase();
                
                return (
                  <Option 
                    key={member.id} 
                    value={member.id.toString()}
                    label={displayName}
                    searchText={searchText}
                    className="!px-0 !py-0 hover:!bg-transparent [&.ant-select-item-option-selected]:!bg-transparent [&.ant-select-item-option-active]:!bg-transparent"
                  >
                    <div className="flex items-center w-full my-1 py-1 px-2 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 mx-1 my-1 rounded-lg gap-2 relative">
                      {currentSelectValues.some(a => a.key === member.id.toString()) && (
                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                          <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      <div className="relative px-3 py-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[12px] font-[600] shadow-sm">
                        {member.user_name ? member.user_name.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-[600] text-gray-900 truncate">{member.user_name}</div>
                        <div className="text-xs text-gray-500 truncate">{member.email}</div>
                      </div>
                    </div>
                  </Option>
                );
              })}
            </Select>
            <div className="mt-1 py-2 text-xs text-gray-500">
              {currentSelectValues.length > 0 
                ? `${currentSelectValues.length} member${currentSelectValues.length !== 1 ? 's' : ''} selected` 
                : 'No members selected'}
            </div>
          </div>
        </div>
      </div>

      {allowToEditFlow && (
        <div className="flex justify-end border-t border-gray-200 pt-2">
          <Button
            type="primary"
            icon={
              <Tick
                width="12"
                height="10"
                strokeWidth="2.5"
                stroke={selectedAssignees.length === 0 ? '#00000040' : 'white'}
              />
            }
            onClick={handleSubmit}
            className={`${selectedAssignees.length === 0 ? 'cursor-not-allowed' : 'cursor-pointer !bg-[#05805c] hover:!bg-[#05805c]'} px-2 !text-[0.813rem] ${isUpdating ? 'opacity-70' : ''}`}
            disabled={isUpdating || selectedAssignees.length === 0}
            loading={isUpdating}
            style={isUpdating ? { pointerEvents: 'none' } : {}}
          >
            OK
          </Button>
        </div>
      )}
    </div>
  );
};

export default AssigneePreview;
